import React, { useEffect } from 'react';

import { Box, Checkbox, FormControlLabel, Paper, Typography, TextField, Grid, Tooltip } from '@material-ui/core';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import Loader from 'components/Loader';
import { actionTemplatesApi } from 'config/api/actionLibrary/actionLibrary';
import { ActionTemplateStep } from 'config/api/actionLibrary/actionLibrary.types';
import action_library_messages from 'messages/action_library_messages';

import { ActionLibraryInstanceFormValues } from '../../ActionLibraryInstanceForm.types';

import useStyles from './styles/StepConfigureSteps.styles';

type StepConfigureStepsProps = {
  validationError?: string | null;
};

const StepConfigureSteps: React.FC<StepConfigureStepsProps> = ({ validationError }) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const { values, setFieldValue, errors } = useFormikContext<ActionLibraryInstanceFormValues>();

  const { data: templateResponse, isLoading } = useQuery(
    ['ACTION_TEMPLATES_GET_SINGLE', values.templateId],
    () => actionTemplatesApi.getSingleActionTemplate(Number(values.templateId)),
    { enabled: !!values.templateId },
  );

  const steps = templateResponse?.data.steps || [];

  // Sync stepOverrides when template has steps but overrides are empty (e.g. navigation/refresh)
  useEffect(() => {
    if (steps.length > 0 && values.stepOverrides.length === 0) {
      setFieldValue(
        'stepOverrides',
        steps.map((step: ActionTemplateStep) => ({
          originalStepId: step.id,
          included: true,
          mandatory: !!step.mandatory,
          reference: step.reference || '',
          relevantFor: step.relevantFor || '',
        })),
      );
    }
  }, [steps.length, values.stepOverrides.length, setFieldValue]);

  const getOverride = (stepId: number) => values.stepOverrides.find(o => o.originalStepId === stepId);

  const getIsMandatory = (stepId: number, originalMandatory?: boolean) => {
    const o = getOverride(stepId);
    return o ? o.mandatory : !!originalMandatory;
  };

  const handleMandatoryToggle = (stepId: number, currentMandatory: boolean) => {
    const newOverrides = values.stepOverrides.map(o => {
      if (o.originalStepId === stepId) {
        return { ...o, mandatory: !currentMandatory };
      }
      return o;
    });
    setFieldValue('stepOverrides', newOverrides);
  };

  const handleReferenceChange = (stepId: number, value: string) => {
    const newOverrides = values.stepOverrides.map(o => {
      if (o.originalStepId === stepId) {
        return { ...o, reference: value };
      }
      return o;
    });
    setFieldValue('stepOverrides', newOverrides);
  };

  const handleRelevantForChange = (stepId: number, value: string) => {
    const newOverrides = values.stepOverrides.map(o => {
      if (o.originalStepId === stepId) {
        return { ...o, relevantFor: value };
      }
      return o;
    });
    setFieldValue('stepOverrides', newOverrides);
  };

  if (isLoading) return <Loader />;

  const stepError = validationError ?? (errors?.stepOverrides ? String(errors.stepOverrides) : null);

  return (
    <Box>
      {stepError && (
        <Typography color='error' paragraph variant='body2'>
          {stepError}
        </Typography>
      )}
      <Box className={styles.infoBox}>
        <Typography variant='body2'>{t(action_library_messages.instance.form.mandatory_not_applicable_info)}</Typography>
      </Box>

      <Box className={styles.section}>
        <Typography gutterBottom variant='h6'>
          {t(action_library_messages.instance.form.steps_section_title)}
        </Typography>
        <Typography color='textSecondary' paragraph variant='body2'>
          {t(action_library_messages.instance.form.steps_section_description)}
        </Typography>

        <Paper style={{ padding: 16 }} variant='outlined'>
          {steps.length === 0 ? (
            <Typography color='textSecondary'>No steps in template</Typography>
          ) : (
            steps.map((step: ActionTemplateStep) => {
              const mandatory = getIsMandatory(step.id, step.mandatory);
              const override = getOverride(step.id);

              return (
                <Box key={step.id} className={styles.stepRow}>
                  <Box className={styles.stepContent}>
                    <Typography variant='subtitle2'>{step.title}</Typography>
                    <Typography color='textSecondary' dangerouslySetInnerHTML={{ __html: step.shortDescription }} variant='body2' />
                    <Box mt={2}>
                      <Grid container spacing={2}>
                        <Grid item sm={6} xs={12}>
                          <TextField
                            fullWidth
                            label={t(action_library_messages.steps.related_reading)}
                            onChange={e => handleReferenceChange(step.id, e.target.value)}
                            size='small'
                            value={override?.reference || ''}
                            variant='outlined'
                          />
                        </Grid>
                        <Grid item sm={6} xs={12}>
                          <TextField
                            fullWidth
                            label={t(action_library_messages.steps.relevant_for)}
                            onChange={e => handleRelevantForChange(step.id, e.target.value)}
                            size='small'
                            value={override?.relevantFor || ''}
                            variant='outlined'
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                  <Box className={styles.stepCheckboxes}>
                    <Tooltip title={t(action_library_messages.instance.form.mandatory_tooltip)}>
                      <FormControlLabel
                        control={
                          <Checkbox checked={mandatory} color='primary' onChange={() => handleMandatoryToggle(step.id, mandatory)} />
                        }
                        label={t(action_library_messages.instance.form.mandatory)}
                      />
                    </Tooltip>
                  </Box>
                </Box>
              );
            })
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default StepConfigureSteps;
